// The scalar functions a view's SQL may call.
//
// IN app/shared RATHER THAN common/code/sql, WHICH IS WHERE ITS SIBLING LIVES.
//
// `tables.js` is in `common/` because the API genuinely needs it — the model
// derives a view's `tables[]` from the query, and that derivation is a security
// boundary that has to happen server-side. This file is the opposite: it is
// browser-only, and it imports @repoflow/components, which is React. A file
// under `common/` is one an api module may import without anyone thinking about
// it, and the cost of that mistake is React in a Lambda bundle. Living here
// makes the constraint structural instead of a comment.
//
// Ported from stack-pkg/src/libs/site-view/index.js, which registered them by
// mutating the alasql module it imported. These take the engine as an argument
// instead, so a test can assert what was registered and two callers cannot race
// to register different sets onto one global.
//
// FIVE OF THE ARCHIVE'S SEVEN. `markdown` and `paragraph` are not here:
//
//   markdown    needs markdown-it, which this estate does not have anywhere —
//               projects/pagews/common/schema/types/params/utils.js records the
//               same gap for its own `markdown` param type.
//   paragraph   needs quill-delta-to-html, which pagews has as an app
//               dependency and nothing shares.
//
// Both existed to turn a source column into HTML for a page template, and this
// service has no page template. They are worth adding only if a view's result is
// ever rendered rather than tabulated. See docs/pending.md.

// Deliberately the one in @repoflow/components rather than a third copy — pagews
// already has a second in common/schema/blog-post/slug.js, and a slug that
// disagrees between two services is a broken URL nobody can attribute.
import { slugify } from "@repoflow/components/types-form";

const isArray = (v) => Array.isArray(v);
const isString = (v) => typeof v === "string";

export const functions = {
  slugify,

  // A bare URL as the { url, description } shape every image-ish value in this
  // platform has.
  urlImage: (url) => ({ url, description: "" }),

  // Airtable's attachment column is an array of objects, each with a
  // `thumbnails` map. This is the one function that knows what an Airtable
  // attachment looks like, which is why it is named for it rather than for what
  // it does.
  //
  // Defensive at every step: a row whose attachment column is empty is ordinary,
  // and the archive's version threw on it (`attachments[0].thumbnails`).
  airtableFirstImage: (attachments) => {
    const first = isArray(attachments) ? attachments[0] : null;
    const url = first?.thumbnails?.large?.url || first?.url || "";
    return { url, description: "" };
  },

  // Airtable's link-to-another-record column is an array of ids even when the
  // link is single. `valueIndex(x, 0)` is what makes it join.
  valueIndex: (array, index) => (isArray(array) ? array[index] : array),

  splitLines: (val) => (isString(val) ? val.split("\n") : val),
};

/**
 * Register the scalars onto an alasql instance.
 *
 * Returns the names registered, so a caller can assert the set rather than
 * trusting that a mutation happened.
 */
export const register = (alasql) => {
  for (const [name, fn] of Object.entries(functions)) {
    alasql.fn[name] = fn;
  }
  return Object.keys(functions);
};

export default { functions, register };
